/*
 * Bib-number display formatting (the my.trumin DISPLAY side of the bib model).
 * Design of record: DESIGN-AUTO-BIB-ALLOCATION-V1 (Duncan-ratified concept
 * 2026-08-10); dispatch BOARDROOM-2026-08-11-222211.
 *
 * my.trumin is the bib TRIGGER + DISPLAY side; settings is the mint AUTHORITY
 * (it allocates the next FCFS number from the event's range and writes
 * bib_number). This module ONLY formats an already-minted bib for display —
 * it never allocates or invents a number.
 *
 * OnePulse ships a flat 0–999 pooled, first-come-first-served, 3-digit range,
 * shown zero-padded ("1" → "001"). Explicit per-ticket-type ranges (e.g. Pro
 * Males 1001–1082) are wider and pass through unpadded. So the rule is: a purely
 * numeric bib is left-padded to a MINIMUM of 3 digits; anything already ≥3
 * digits, or non-numeric, passes through unchanged. Absent ⇒ null (the ticket
 * omits the Bib field honestly — never a fabricated placeholder).
 */

/** Zero-pad a minted numeric bib to ≥3 digits ("1" → "001", "42" → "042",
 *  "1001" → "1001"); pass through non-numeric values; null when absent/blank. */
export function formatBib(bib: string | number | null | undefined): string | null {
  if (bib === null || bib === undefined) return null
  const raw = String(bib).trim()
  if (!raw) return null
  // Purely-numeric bib → zero-pad to a minimum of 3 digits (OnePulse 3-digit).
  if (/^\d+$/.test(raw)) return raw.padStart(3, '0')
  // Already-formatted or alphanumeric range bib → passthrough unchanged.
  return raw
}

/** The ticket-face label for a minted bib, e.g. "Bib number: 001"; null when
 *  the ticket has no bib yet (pre-allocation) so the field is omitted. */
export function bibLabel(bib: string | number | null | undefined): string | null {
  const formatted = formatBib(bib)
  return formatted === null ? null : `Bib number: ${formatted}`
}
